<template>
  <div v-loading="loading" class="users-page">
    <div class="users-page__top">
      <div class="users-page__title">Users</div>
    </div>
    <div class="users-page__main">
      <div class="users-page__content">
        <div class="users-page__content-top">
          <div class="users-page__search-filter">
            <el-input
              v-model="filter.email"
              placeholder="Search"
              size="small"
              clearable
              style="width: 240px"
              @clear="getUsers"
              @keypress.enter="getUsers"
            />
            <el-button
              type="primary"
              :icon="Search"
              size="small"
              @click="getUsers"
            ></el-button>
          </div>
        </div>
        <el-table
          :data="list"
          height="100%"
          style="width: 100%; border-radius: 4px"
          @row-click="onClickCeil"
        >
          <template #empty>
            <EmptyData class="permissions-page__table-empty" />
          </template>
          <el-table-column prop="id" label="#" width="70">
            <template #default="scope">
              {{ scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="email" label="Email" />
          <el-table-column prop="subscription_type" label="Subscription Type">
            <template #default="scope">
              <div class="users-page__first-l-upper">
                {{ scope.row.subscription_type || "-" }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="130">
            <template #default="scope">
              <el-tag
                class="users-page__first-l-upper"
                :type="getTypeByStatus(scope.row.status)"
                >{{ scope.row.status || "None" }}</el-tag
              >
            </template>
          </el-table-column>
        </el-table>
        <div class="users-page__pagination">
          <el-pagination
            layout="prev, pager, next"
            v-model:current-page="filter.page"
            :total="users.total"
            :page-size="20"
            background
            @current-change="getUsers"
          />
        </div>
      </div>
      <div class="users-page__side">
        <ViewInspector
          :field="user"
          :vip="user.is_vip"
          @on-refresh="handleRefresh"
          @on-toggle-vip="handleToggleVip"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Search } from "@element-plus/icons-vue";
import { EmptyData } from "@/shared/ui/components/EmptyData";
import { ViewInspector } from "@/shared/ui/components/ViewInspector";
import { ElMessage } from "element-plus";
import { IUsers, IUser } from "@/shared/types/users";

const store = useStore();
const route = useRoute();
const router = useRouter();

const loading = ref<boolean>(false);
const currentId = ref<string | null>(null);

const filter = reactive({
  email: "",
  page: 1,
});

const users = computed<IUsers>(() => {
  return store.state.users.data;
});

const list = computed<IUser[]>(() => {
  return users.value.data;
});

const user = computed(() => {
  if (currentId.value) {
    const item =
      list.value.find((user: IUser) => user.uuid === currentId.value) || {};
    return item;
  } else {
    return {};
  }
});

const subscriptions = computed(() => {
  return user.value.subscriptions;
});

onMounted(async () => {
  try {
    await getUsers();
    currentId.value = list.value[0].uuid;
    try {
      await getUser(currentId.value);
    } catch (e: any) {
      ElMessage({
        message: e,
        type: "error",
      });
    }
  } catch (e: any) {
    ElMessage({
      message: e,
      type: "error",
    });
  }
});

const getUsers = async () => {
  loading.value = true;
  await store.dispatch("users/getUsers", {
    email: filter.email,
    page: filter.page,
  });
  loading.value = false;
};

const handleRefresh = async () => {
  loading.value = true;
  await store.dispatch("users/refreshUser", { user_uuid: currentId.value });
  if (currentId.value) {
    await getUser(currentId.value);
  }
  await getUsers();
  loading.value = false;
};

const handleToggleVip = async () => {
  loading.value = true;
  console.log("handle toggleVip");
  console.log(currentId.value);
  await store.dispatch("users/toggleVip", { user_uuid: currentId.value });
  if (currentId.value) {
    await getUser(currentId.value);
  }
  await getUsers();
  loading.value = false;
};

const getUser = async (id: string) => {
  return await store.dispatch("users/getUser", id);
};

const getTypeByStatus = (status: string) => {
  switch (status) {
    case "vip":
      return "primary";
    case "expired":
      return "danger";
    case "active":
      return "success";
    case "none":
      return "info";
    default:
      return "info";
  }
};

const onClickCeil = (item: IUser) => {
  currentId.value = item.uuid;
  getUser(currentId.value);
};
</script>

<style lang="scss">
.users-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 24px;
    height: 56px;
    flex-shrink: 0;
    margin-bottom: 16px;
  }
  &__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 26px;
  }
  &__top-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__main {
    display: flex;
    flex-grow: 1;
    overflow: hidden;
  }
  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: 100%;
    height: 100%;
    border-radius: var(--Radius-border-radius-base, 4px);
    background: var(--Color-Background-bg-color, #fff);
  }
  &__content-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid var(--Color-Border-border-color-lighter, #ebeef5);
  }
  &__search-filter {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  &__filters {
    display: flex;
    align-items: center;
    gap: 16px;
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  &__filters-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__button-link .el-icon {
    color: #409eff;
  }
  &__app-title {
    color: var(--Color-Primary-color-primary, #409eff);
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }
  &__app-subtitle {
    color: var(--Color-Text-text-color-regular, #606266);
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
  &__side {
    position: relative;
    flex-shrink: 0;
    height: 100%;
    width: 344px;
    padding: 0 24px;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  &__side-line {
    height: 1px;
    background: var(--Color-Border-border-color-light, #e4e7ed);
    margin: 16px 0;
  }
  &__table-empty {
    margin: 0 auto;
  }
  &__status {
    display: flex;
    align-items: center;
  }
  &__status-close {
    margin-left: 8px;
  }
  &__status-arrow {
    cursor: pointer;
    margin-left: 8px !important;
  }
  &__dropdown-item-icon {
    display: none !important;
  }
  &__dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    span {
      color: var(--Color-Text-text-color-regular, #606266);
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    }
    &_active {
      span {
        color: var(--Color-Primary-color-primary, #409eff);
      }
      .users-page__dropdown-item-icon {
        display: block !important;
      }
    }
  }
  &__dropdown-item-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__dropdown-item-image {
    width: var(--Size-common-component-size-small, 24px);
    height: var(--Size-common-component-size-small, 24px);
    flex-shrink: 0;
  }
  &__header-column {
    .cell {
      display: flex;
      align-items: center;
    }
  }
  &__first-l-upper {
    &::first-letter {
      text-transform: uppercase;
    }
    span::first-letter {
      text-transform: uppercase;
    }
  }
  &__all-status {
    .users-page__status-arrow {
      display: none;
    }
  }

  .table-row {
    &:hover {
      .users-page__all-status .users-page__status-arrow {
        display: block;
      }
    }
  }

  .circle {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 100%;
  }

  &__pagination {
    padding: 28px 6px;
    background: var(--Color-Background-bg-color-page, #f2f3f5);
  }
}
</style>
